// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE NOTIFICATION TRAY FOLLOWS THE BELL (Hannah, 2026-11-01 — the phone's icon badge kept its count after the alerts
// were read in the panel).
//
// The Badge plugin is not in the shipped binaries (iOS 1.0.2, Android 1.0.4), so Badge.set is a no-op there. On Android
// most launchers show the icon badge from the app's DELIVERED NOTIFICATIONS, so removing the notifications of alerts the
// borrower has already seen drops the badge without a native build — through @capacitor-firebase/messaging, which IS in
// both binaries (getDeliveredNotifications / removeDeliveredNotifications).
//
// 🔑 THE MATCH IS THE ALERT ID. The server tags every push's Android notification `doorly-alert-<id>` and carries the id
// in the push data (backend utils/sendPushNotification). On Android the plugin reports a delivered notification's `tag`
// (and its extras as `data`); FCM keeps the push's data payload in the tap intent, not in the extras, so the tag is the
// reliable key and data.alertId the fallback.
//
// Pure: no imports, so backend/test can evaluate it (test/helpers/loadFrontendModule).
// ─────────────────────────────────────────────────────────────────────────────

export const ALERT_TAG_PREFIX = 'doorly-alert-';

/** The alert id a delivered notification belongs to, or null (not one of ours / no id). */
export function alertIdOfNotification(n) {
  if (!n) return null;
  const tag = String(n.tag || '');
  if (tag.startsWith(ALERT_TAG_PREFIX)) {
    const id = Number(tag.slice(ALERT_TAG_PREFIX.length));
    if (Number.isInteger(id) && id > 0) return id;
  }
  const d = n.data && typeof n.data === 'object' ? n.data : {};
  const raw = d.alertId != null ? d.alertId : d.alert_id;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * The delivered notifications to remove: those whose alert the borrower has seen or read, or which the list no longer
 * shows (cleared, or out of its window). A notification with no alert id is left alone (count 0 removes everything).
 * @param {Array<{id:string, tag?:string, data?:object}>} delivered  FirebaseMessaging.getDeliveredNotifications().notifications
 * @param {Array<{id:number, seen?:number|boolean, is_read?:number|boolean}>} alerts  GET /api/alerts .alerts
 * @param {{complete?:boolean}} [o]  complete: the list holds every alert (alerts.length === total). When it does not, an
 *   alert missing from the page is unknown, not cleared — its notification stays.
 */
export function notificationsToRemove(delivered, alerts, { complete = true } = {}) {
  const byId = new Map((alerts || []).map((a) => [Number(a.id), a]));
  return (delivered || []).filter((n) => {
    const id = alertIdOfNotification(n);
    if (id == null) return false;
    const a = byId.get(id);
    if (!a) return !!complete;
    return !!a.seen || !!a.is_read;
  });
}
